import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function BestSellersPage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Top 10 Trusted Kakobuy Sellers in Our Spreadsheet for 2026",
    "description": "Discover the most reliable Kakobuy sellers vetted by our community spreadsheet, featuring detailed seller profiles, ratings, and product quality assessments.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/best-sellers"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20gold%20star%20rating%20icon%20and%20a%20clipboard%20with%20seller%20profiles%20on%20a%20clean%20white%20desk%20alongside%20a%20coffee%20cup%20and%20a%20smartphone%20displaying%20a%20shopping%20app%20soft%20warm%20morning%20light%20from%20a%20side%20window%20editorial%20business%20photography%20with%20friendly%20atmosphere&width=1400&height=600&seq=article-sellers-v2-03&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">Trusted Sellers</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              Top 10 Trusted <span className="text-primary-500">Kakobuy Sellers</span> in Our Spreadsheet 2026
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>5 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20gold%20star%20rating%20icon%20and%20a%20clipboard%20with%20seller%20profiles%20on%20a%20clean%20white%20desk%20alongside%20a%20coffee%20cup%20and%20a%20smartphone%20displaying%20a%20shopping%20app%20soft%20warm%20morning%20light%20from%20a%20side%20window%20editorial%20business%20photography%20with%20friendly%20atmosphere&width=1400&height=600&seq=article-sellers-v2-03&orientation=landscape"
                alt="Top Trusted Kakobuy Sellers" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">How We Identify Trusted Sellers</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Finding reliable sellers on Kakobuy is the key to a great shopping experience. Our <strong>Kakobuy Spreadsheet</strong> uses a rigorous vetting process that evaluates sellers based on transaction history, customer feedback percentage, response time, product consistency, and community reputation. Only sellers who meet our strict criteria make it into the spreadsheet.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                The <strong>Kakobuy Spreadsheet</strong> includes a trust score for each seller, making it easy to identify the most reliable partners at a glance. Sellers with scores above 95% have demonstrated exceptional reliability across hundreds or thousands of transactions, while those below 80% are excluded from our recommendations entirely.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Top Seller Categories</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { cat: "Sneaker Specialists", rating: "98%", desc: "Sellers focused exclusively on footwear with detailed sizing guides and consistent quality." },
                  { cat: "Streetwear Experts", rating: "96%", desc: "Specializing in hoodies, tees, and casual wear with fast communication and reliable shipping." },
                  { cat: "Accessory Artisans", rating: "94%", desc: "Bags, belts, and accessories from sellers who consistently deliver excellent craftsmanship." },
                  { cat: "Lifestyle Vendors", rating: "93%", desc: "Diverse product ranges including sets, outerwear, and seasonal collections with strong feedback." },
                ].map((s, i) => (
                  <div key={i} className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-foreground-900">{s.cat}</h3>
                      <span className="text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full">{s.rating}</span>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}